/**
 * Your last few appetite questions, on the front door — folded away.
 *
 * Josh, 2026-09-07: "recent searches should be on the dashboard as well. Like,
 * you can see on the dashboard a recent search and click on it to pick back up
 * on that conversation. Or click in connor, see the recent searches and pick
 * back up there."
 *
 * ⚠️ COLLAPSED BY DEFAULT, AND THE STATE IS DELIBERATELY NOT REMEMBERED (Josh,
 * 2026-09-08: "Recent Searches (3) and then you click on it to expand to see
 * them all instead of having a big thing there at all times"). Persisting
 * "expanded" would put the big thing back on the page every morning, which is
 * precisely what was asked away. Opening it is a decision you make when you
 * want it, and it costs one click.
 *
 * ⚠️ THE COUNT IS WHAT WILL ACTUALLY OPEN, NOT WHAT IS STORED. The list caps at
 * five; a header reading "(9)" that unfolds into five rows is a small lie on the
 * one element whose whole job is telling you what you already saw.
 *
 * ⚠️ BOTH DOORS, ONE REPLAY. Clicking a row fires the same function Connor's own
 * "Recent searches" list fires — it opens the chat with that answer already on
 * screen. Two code paths putting a past answer back would eventually put two
 * different things back, and the whole promise of this feature is that what you
 * see is what you were shown.
 *
 * ⚠️ REPLAYED, NEVER RE-RUN. Re-running costs a call and can return something
 * different, because the appetite bundle is refreshed periodically. "What did
 * it tell me about that roofer on Tuesday" has exactly one honest answer.
 *
 * ⚠️ IT HIDES ITSELF WHEN EMPTY. A permanent "no recent searches" card on the
 * page somebody opens every morning is furniture, and the front door already
 * has to fight to keep the actual products visible.
 */
import { useEffect, useState } from 'react';
import { openConnor } from './AppetiteChat';
import { ago, loadRecent, type RecentSearch } from '../lib/recentSearches';

/** What the header offers to open. Keep in step with the slice below. */
const MAX_SHOWN = 5;

export function RecentSearchesCard(): JSX.Element | null {
  const [recent, setRecent] = useState<RecentSearch[]>([]);
  const [open, setOpen] = useState(false);

  /*
    Read on mount rather than held in a store: this lives in localStorage and
    the only thing that writes it is Connor, who is on the same page. Coming
    back to the dashboard remounts this and picks up anything asked since.
  */
  useEffect(() => setRecent(loadRecent()), []);

  if (!recent.length) return null;

  const shown = recent.slice(0, MAX_SHOWN);

  return (
    <section className="rs-card">
      <button
        type="button"
        className="rs-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="rs-recent"
      >
        <span className="rs-title">
          Recent searches <span className="rs-n">({shown.length})</span>
        </span>
        {/* Same chevron idiom as the loss run rows: rotates on aria-expanded. */}
        <svg className="rs-chev" viewBox="0 0 24 24" aria-hidden="true">
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>

      {/*
        Rendered always and hidden with the attribute rather than swapped out, so
        aria-controls points at something real whether it is open or shut.
      */}
      <div className="rs-open" id="rs-recent" hidden={!open}>
        <p className="rs-cap">The answer you were actually shown, not a fresh one.</p>
        <ul className="rs-list">
          {shown.map((r) => {
            const carriers = r.answer.classes?.[0]?.class;
            return (
              <li key={r.at}>
                <button type="button" onClick={() => openConnor(r.at)}>
                  <span className="rs-q">{r.question}</span>
                  <span className="rs-meta">
                    {carriers && <span className="rs-cls">{carriers}</span>}
                    <span>{ago(r.at)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
